import { denormalize } from 'normalizr';
import { createSelector } from 'reselect';
import {
  project as projectSchema,
  reportsBarChartItem as reportsBarChartItemSchema,
} from '../lib/schema.js';
import { CALL_API, HTTP_METHODS } from '../middlewares/api.js';
import { selectCurrentOrganizationId } from './auth.js';
import { selectEntities } from './entities.js';

// API Actions
const RETRIEVE_PROJECTS_REQUEST =
  'changesync/projects/RETRIEVE_PROJECTS_REQUEST';
const RETRIEVE_PROJECTS_SUCCESS =
  'changesync/projects/RETRIEVE_PROJECTS_SUCCESS';
const RETRIEVE_PROJECTS_FAILURE =
  'changesync/projects/RETRIEVE_PROJECTS_FAILURE';

const RETRIEVE_PROJECT_REQUEST = 'changesync/projects/RETRIEVE_PROJECT_REQUEST';
const RETRIEVE_PROJECT_SUCCESS = 'changesync/projects/RETRIEVE_PROJECT_SUCCESS';
const RETRIEVE_PROJECT_FAILURE = 'changesync/projects/RETRIEVE_PROJECT_FAILURE';
const RETRIEVE_PLAN_STATUS_REQUEST =
  'changesync/projects/RETRIEVE_PLAN_STATUS_REQUEST';
const RETRIEVE_PLAN_STATUS_SUCCESS =
  'changesync/projects/RETRIEVE_PLAN_STATUS_SUCCESS';
const RETRIEVE_PLAN_STATUS_FAILURE =
  'changesync/projects/RETRIEVE_PLAN_STATUS_FAILURE';
const CREATE_PROJECT_REQUEST = 'changesync/projects/CREATE_PROJECT_REQUEST';
const CREATE_PROJECT_SUCCESS = 'changesync/projects/CREATE_PROJECT_SUCCESS';
const CREATE_PROJECT_FAILURE = 'changesync/projects/CREATE_PROJECT_FAILURE';
const UPDATE_PROJECT_REQUEST = 'changesync/projects/UPDATE_PROJECT_REQUEST';
const UPDATE_PROJECT_SUCCESS = 'changesync/projects/UPDATE_PROJECT_SUCCESS';
const UPDATE_PROJECT_FAILURE = 'changesync/projects/UPDATE_PROJECT_FAILURE';
const RETRIEVE_BAR_CHART_DATA_REQUEST =
  'changesync/projects/RETRIEVE_BAR_CHART_DATA_REQUEST';
const RETRIEVE_BAR_CHART_DATA_SUCCESS =
  'changesync/projects/RETRIEVE_BAR_CHART_DATA_SUCCESS';
const RETRIEVE_BAR_CHART_DATA_FAILURE =
  'changesync/projects/RETRIEVE_BAR_CHART_DATA_FAILURE';
const RESET_SEARCH_RESULTS = 'changesync/projects/RESET_SEARCH_RESULTS';

const DELETE_PROJECT_REQUEST = 'changesync/projects/DELETE_PROJECT_REQUEST';
const DELETE_PROJECT_SUCCESS = 'changesync/projects/DELETE_PROJECT_SUCCESS';
const DELETE_PROJECT_FAILURE = 'changesync/projects/DELETE_PROJECT_FAILURE';

// Initial State
const initialState = {
  retrieveProjects: {
    projectIds: [],
    pending: false,
    success: false,
    error: null,
  },
  retrieveProject: {
    pending: false,
    success: false,
    error: null,
  },
  /**
   * @property {object} initiativeIds
   * @example
   * {
   *   [projectId]: {
   *     communicationInitiatives: [initiativeId, initiativeId, ...]
   *     readinessInitiatives: [initiativeId, initiativeId, ...]
   *     trainingInitiatives: [initiativeId, initiativeId, ...]
   *   }
   * }
   */
  retrieveBarChartData: {
    itemsIds: [],
    pending: false,
    success: false,
    error: null,
  },
};

// Reducer
export default (state = initialState, action) => {
  switch (action.type) {
    case RETRIEVE_PROJECTS_REQUEST:
      return {
        ...state,
        retrieveProjects: {
          ...state.retrieveProjects,
          pending: true,
          success: false,
          error: null,
        },
      };
    case RETRIEVE_PROJECTS_SUCCESS:
      return {
        ...state,
        retrieveProjects: {
          ...state.retrieveProjects,
          projectIds: action.payload.result,
          pending: false,
          success: true,
        },
      };
    case RETRIEVE_PROJECTS_FAILURE:
      return {
        ...state,
        retrieveProjects: {
          ...state.retrieveProjects,
          pending: false,
          error: action.error,
        },
      };
    case RETRIEVE_PROJECT_REQUEST:
      return {
        ...state,
        retrieveProject: {
          ...state.retrieveProject,
          pending: true,
          success: false,
          error: null,
        },
      };
    case RETRIEVE_PROJECT_SUCCESS:
      return {
        ...state,
        retrieveProject: {
          ...state.retrieveProject,
          pending: false,
          success: true,
        },
      };
    case RETRIEVE_PROJECT_FAILURE:
      return {
        ...state,
        retrieveProject: {
          ...state.retrieveProject,
          pending: false,
          error: action.error,
        },
      };

    case RETRIEVE_BAR_CHART_DATA_REQUEST:
      return {
        ...state,
        retrieveBarChartData: {
          ...state.retrieveBarChartData,
          pending: true,
          success: false,
          error: null,
        },
      };
    case RETRIEVE_BAR_CHART_DATA_SUCCESS:
      return {
        ...state,
        retrieveBarChartData: {
          ...state.retrieveBarChartData,
          itemsIds: action.payload.result,
          pending: false,
          success: true,
        },
      };
    case RETRIEVE_BAR_CHART_DATA_FAILURE:
      return {
        ...state,
        retrieveBarChartData: {
          ...state.retrieveBarChartData,
          pending: false,
          error: action.error,
        },
      };

    case RESET_SEARCH_RESULTS:
      return {
        ...state,
        searchProjects: {
          ...initialState.searchProjects,
        },
      };
    default:
      return state;
  }
};

// Action Creators

export const retrieveProjects = (query) => (dispatch, getState) => {
  const organizationId = selectCurrentOrganizationId(getState());

  return dispatch({
    [CALL_API]: {
      types: [
        RETRIEVE_PROJECTS_REQUEST,
        RETRIEVE_PROJECTS_SUCCESS,
        RETRIEVE_PROJECTS_FAILURE,
      ],
      endpoint: `organizations/${organizationId}/projects`,
      query,
      method: HTTP_METHODS.GET,
      schema: [projectSchema],
    },
  });
};

export const retrieveProject = (id, initiatives = true) => ({
  [CALL_API]: {
    types: [
      RETRIEVE_PROJECT_REQUEST,
      RETRIEVE_PROJECT_SUCCESS,
      RETRIEVE_PROJECT_FAILURE,
    ],
    endpoint: `projects/${id}`,
    method: HTTP_METHODS.GET,
    schema: projectSchema,
    query: { initiatives },
  },
});

export const createProject = (project) => (dispatch, getState) => {
  const organizationId = selectCurrentOrganizationId(getState());

  return dispatch({
    [CALL_API]: {
      types: [
        CREATE_PROJECT_REQUEST,
        CREATE_PROJECT_SUCCESS,
        CREATE_PROJECT_FAILURE,
      ],
      endpoint: `organizations/${organizationId}/projects`,
      method: HTTP_METHODS.POST,
      schema: projectSchema,
      body: { project },
    },
  });
};

export const updateProject = (id, project) => ({
  [CALL_API]: {
    types: [
      UPDATE_PROJECT_REQUEST,
      UPDATE_PROJECT_SUCCESS,
      UPDATE_PROJECT_FAILURE,
    ],
    endpoint: `projects/${id}`,
    method: HTTP_METHODS.PUT,
    schema: projectSchema,
    body: { project },
  },
});

export const retrievePlanStatus = () => (dispatch, getState) => {
  const organizationId = selectCurrentOrganizationId(getState());

  return dispatch({
    [CALL_API]: {
      types: [
        RETRIEVE_PLAN_STATUS_REQUEST,
        RETRIEVE_PLAN_STATUS_SUCCESS,
        RETRIEVE_PLAN_STATUS_FAILURE,
      ],
      endpoint: `organizations/${organizationId}/can_create_project`,
      method: HTTP_METHODS.GET,
    },
  });
};

export const retrieveBarChartData = (query) => (dispatch, getState) => {
  const { status, ...params } = query;
  const organizationId = selectCurrentOrganizationId(getState());

  return dispatch({
    [CALL_API]: {
      types: [
        RETRIEVE_BAR_CHART_DATA_REQUEST,
        RETRIEVE_BAR_CHART_DATA_SUCCESS,
        RETRIEVE_BAR_CHART_DATA_FAILURE,
      ],
      endpoint: `organizations/${organizationId}/projects/bar_chart`,
      method: HTTP_METHODS.GET,
      query: {
        ...params,
        status: status === 'all' ? undefined : status,
      },
      schema: [reportsBarChartItemSchema],
    },
    meta: {
      query,
    },
  });
};

export const deleteProject = (id) => ({
  [CALL_API]: {
    types: [
      DELETE_PROJECT_REQUEST,
      DELETE_PROJECT_SUCCESS,
      DELETE_PROJECT_FAILURE,
    ],
    endpoint: `projects/${id}`,
    method: HTTP_METHODS.DELETE,
  },
});

export const retrieveIsShowAlertOnDelete = (id) => ({
  [CALL_API]: {
    types: [
      DELETE_PROJECT_REQUEST,
      DELETE_PROJECT_SUCCESS,
      DELETE_PROJECT_FAILURE,
    ],
    endpoint: `projects/${id}/show_alert`,
    method: HTTP_METHODS.GET,
  },
});

// Selectors

export const selectRetrieveProjectsState = (state) =>
  state.projects.retrieveProjects;
export const selectRetrieveReportsBarChartDataState = (state) =>
  state.projects.retrieveBarChartData;
export const selectRetrieveProjectState = (state) =>
  state.projects.retrieveProject;

export const selectProject = (id) => (state) =>
  denormalize(id, projectSchema, state.entities);

const selectProjectIds = createSelector(
  selectRetrieveProjectsState,
  (state) => state.projectIds
);

const selectBarChartDataIds = createSelector(
  selectRetrieveReportsBarChartDataState,
  (state) => state.itemsIds
);

export const selectReportsBarChartItems = createSelector(
  selectBarChartDataIds,
  selectEntities,
  (itemsIds, entities) =>
    denormalize(itemsIds, [reportsBarChartItemSchema], entities)
);

export const selectSearchProjectsResults = createSelector(
  selectProjectIds,
  selectEntities,
  (projectIds, entities) => denormalize(projectIds, [projectSchema], entities)
);

export const selectProjects = createSelector(
  selectProjectIds,
  selectEntities,
  (projectIds, entities) => denormalize(projectIds, [projectSchema], entities)
);

// Projects that can be picked: a placeholder of a running duplication cannot be used yet.
export const selectReadyProjects = createSelector(selectProjects, (projects) =>
  projects.filter((project) => !project.duplicating)
);
